import { useState, useCallback, useMemo } from 'react';
import Papa from 'papaparse';

export type ImportStep =
  | 'select-entity'
  | 'upload'
  | 'mapping'
  | 'preview'
  | 'importing'
  | 'complete';
export type EntityType = 'leads' | 'accounts' | 'contacts';

// Field configuration for each entity type
export interface FieldConfig {
  key: string;
  label: string;
  required: boolean;
  autoMapKeys: string[];
}

export const ENTITY_FIELDS: Record<EntityType, FieldConfig[]> = {
  leads: [
    {
      key: 'name',
      label: 'Name',
      required: true,
      autoMapKeys: ['name', 'full name', 'contact name', 'lead name'],
    },
    {
      key: 'company',
      label: 'Company',
      required: true,
      autoMapKeys: ['company', 'company name', 'organization'],
    },
    {
      key: 'email',
      label: 'Email',
      required: false,
      autoMapKeys: ['email', 'email address', 'e-mail'],
    },
    {
      key: 'phone',
      label: 'Phone',
      required: false,
      autoMapKeys: ['phone', 'phone number', 'telephone'],
    },
    {
      key: 'title',
      label: 'Title',
      required: false,
      autoMapKeys: ['title', 'job title', 'position'],
    },
    {
      key: 'source',
      label: 'Source',
      required: false,
      autoMapKeys: ['source', 'lead source', 'origin'],
    },
  ],
  accounts: [
    {
      key: 'name',
      label: 'Name',
      required: true,
      autoMapKeys: ['name', 'account name', 'company', 'company name'],
    },
    { key: 'website', label: 'Website', required: false, autoMapKeys: ['website', 'url', 'web'] },
    {
      key: 'phone',
      label: 'Phone',
      required: false,
      autoMapKeys: ['phone', 'phone number', 'telephone'],
    },
    { key: 'industry', label: 'Industry', required: false, autoMapKeys: ['industry', 'sector'] },
    { key: 'type', label: 'Type', required: false, autoMapKeys: ['type', 'account type'] },
  ],
  contacts: [
    {
      key: 'name',
      label: 'Name',
      required: true,
      autoMapKeys: ['name', 'full name', 'contact name'],
    },
    {
      key: 'account',
      label: 'Account',
      required: true,
      autoMapKeys: ['account', 'account name', 'company'],
    },
    {
      key: 'email',
      label: 'Email',
      required: false,
      autoMapKeys: ['email', 'email address', 'e-mail'],
    },
    {
      key: 'phone',
      label: 'Phone',
      required: false,
      autoMapKeys: ['phone', 'phone number', 'telephone'],
    },
    {
      key: 'title',
      label: 'Title',
      required: false,
      autoMapKeys: ['title', 'job title', 'position'],
    },
  ],
};

// Auto-map CSV headers to entity fields
function autoMapFields(headers: string[], entityType: EntityType): Record<string, string> {
  const mapping: Record<string, string> = {};
  const fields = ENTITY_FIELDS[entityType];
  const usedFields = new Set<string>();

  for (const header of headers) {
    const normalizedHeader = header.toLowerCase().trim();
    for (const field of fields) {
      if (!usedFields.has(field.key) && field.autoMapKeys.includes(normalizedHeader)) {
        mapping[header] = field.key;
        usedFields.add(field.key);
        break;
      }
    }
  }
  return mapping;
}

export interface ImportProgress {
  currentBatch: number;
  totalBatches: number;
  processedRows: number;
  totalRows: number;
}

export interface ImportError {
  row: number;
  message: string;
}

export interface ImportResult {
  success: number;
  failed: number;
  errors: ImportError[];
}

export interface ImportState {
  step: ImportStep;
  entityType: EntityType | null;
  file: File | null;
  parsedData: Record<string, string>[] | null;
  headers: string[] | null;
  fieldMapping: Record<string, string>;
  importProgress: ImportProgress | null;
  importResult: ImportResult | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: ImportState = {
  step: 'select-entity',
  entityType: null,
  file: null,
  parsedData: null,
  headers: null,
  fieldMapping: {},
  importProgress: null,
  importResult: null,
  isLoading: false,
  error: null,
};

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const BATCH_SIZE = 50; // BR-IMPORT001: Process in batches of 50 records

// Helper function to chunk array into batches
function chunkArray<T>(array: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
}

export function useImport() {
  const [state, setState] = useState<ImportState>(initialState);

  const setEntityType = useCallback((type: EntityType) => {
    setState(prev => ({
      ...prev,
      entityType: type,
      step: 'upload',
      error: null,
    }));
  }, []);

  const handleFileSelect = useCallback((file: File) => {
    // Validate file type
    if (!file.name.endsWith('.csv')) {
      setState(prev => ({
        ...prev,
        error: 'Invalid file type. Please upload a CSV file.',
      }));
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setState(prev => ({
        ...prev,
        error: 'File is too large. Maximum size is 5MB.',
      }));
      return;
    }

    setState(prev => ({
      ...prev,
      file,
      isLoading: true,
      error: null,
    }));

    // Parse CSV with PapaParse
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: results => {
        const headers = results.meta.fields || [];
        const data = results.data as Record<string, string>[];

        if (data.length === 0) {
          setState(prev => ({
            ...prev,
            isLoading: false,
            error: 'CSV file is empty or contains no data rows.',
          }));
          return;
        }

        // Auto-map fields based on header names
        setState(prev => {
          const autoMapping = prev.entityType ? autoMapFields(headers, prev.entityType) : {};
          return {
            ...prev,
            headers,
            parsedData: data,
            fieldMapping: autoMapping,
            step: 'mapping',
            isLoading: false,
            error: null,
          };
        });
      },
      error: error => {
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: `Failed to parse CSV: ${error.message}`,
        }));
      },
    });
  }, []);

  const setFieldMapping = useCallback((csvColumn: string, entityField: string | null) => {
    setState(prev => {
      const newMapping = { ...prev.fieldMapping };
      if (entityField === null) {
        delete newMapping[csvColumn];
      } else {
        // Remove any existing mapping to this field (one-to-one mapping)
        for (const [col, field] of Object.entries(newMapping)) {
          if (field === entityField && col !== csvColumn) {
            delete newMapping[col];
          }
        }
        newMapping[csvColumn] = entityField;
      }
      return { ...prev, fieldMapping: newMapping };
    });
  }, []);

  // Get list of unmapped required fields
  const getUnmappedRequiredFields = useCallback((): string[] => {
    if (!state.entityType) return [];
    const fields = ENTITY_FIELDS[state.entityType];
    const mappedFields = new Set(Object.values(state.fieldMapping));
    return fields.filter(f => f.required && !mappedFields.has(f.key)).map(f => f.label);
  }, [state.entityType, state.fieldMapping]);

  const proceedToPreview = useCallback(() => {
    const unmapped = getUnmappedRequiredFields();
    if (unmapped.length > 0) {
      setState(prev => ({
        ...prev,
        error: `Required fields not mapped: ${unmapped.join(', ')}`,
      }));
      return;
    }
    setState(prev => ({ ...prev, step: 'preview', error: null }));
  }, [getUnmappedRequiredFields]);

  // Get preview data (first N rows transformed via mapping)
  const getPreviewData = useCallback(
    (maxRows = 5): Record<string, string>[] => {
      if (!state.parsedData || !state.entityType) return [];

      return state.parsedData.slice(0, maxRows).map(row => {
        const mappedRow: Record<string, string> = {};
        for (const [csvCol, entityField] of Object.entries(state.fieldMapping)) {
          if (entityField && row[csvCol] !== undefined) {
            mappedRow[entityField] = row[csvCol];
          }
        }
        return mappedRow;
      });
    },
    [state.parsedData, state.fieldMapping, state.entityType]
  );

  // Row validation result interface
  interface RowValidation {
    rowIndex: number;
    isValid: boolean;
    missingFields: string[];
  }

  // Validate all rows against required fields
  const validateRows = useCallback((): {
    valid: number;
    invalid: number;
    errors: RowValidation[];
  } => {
    if (!state.parsedData || !state.entityType) return { valid: 0, invalid: 0, errors: [] };

    const fields = ENTITY_FIELDS[state.entityType];
    const requiredFields = fields.filter(f => f.required);
    const errors: RowValidation[] = [];
    let valid = 0;
    let invalid = 0;

    state.parsedData.forEach((row, index) => {
      const missingFields: string[] = [];

      for (const field of requiredFields) {
        const csvColumn = Object.entries(state.fieldMapping).find(([, f]) => f === field.key)?.[0];
        if (!csvColumn || !row[csvColumn]?.trim()) {
          missingFields.push(field.label);
        }
      }

      if (missingFields.length > 0) {
        invalid++;
        errors.push({ rowIndex: index, isValid: false, missingFields });
      } else {
        valid++;
      }
    });

    return { valid, invalid, errors };
  }, [state.parsedData, state.entityType, state.fieldMapping]);

  const goBack = useCallback(() => {
    setState(prev => {
      if (prev.step === 'mapping') {
        return {
          ...prev,
          step: 'upload',
          parsedData: null,
          headers: null,
          fieldMapping: {},
          error: null,
        };
      }
      if (prev.step === 'upload') {
        return {
          ...prev,
          step: 'select-entity',
          file: null,
          parsedData: null,
          headers: null,
          fieldMapping: {},
          error: null,
        };
      }
      if (prev.step === 'preview') {
        return {
          ...prev,
          step: 'mapping',
          error: null,
        };
      }
      return prev;
    });
  }, []);

  const reset = useCallback(() => {
    setState(initialState);
  }, []);

  // Computed: check if can proceed from mapping
  const canProceedFromMapping = useMemo(() => {
    if (!state.entityType) return false;
    const fields = ENTITY_FIELDS[state.entityType];
    const mappedFields = new Set(Object.values(state.fieldMapping));
    return fields.filter(f => f.required).every(f => mappedFields.has(f.key));
  }, [state.entityType, state.fieldMapping]);

  // Get valid rows transformed with field mapping for import
  const getValidRowsForImport = useCallback((): Record<string, string>[] => {
    if (!state.parsedData || !state.entityType) return [];

    const fields = ENTITY_FIELDS[state.entityType];
    const requiredFields = fields.filter(f => f.required);

    return state.parsedData
      .filter(row => {
        // Check all required fields have values
        for (const field of requiredFields) {
          const csvColumn = Object.entries(state.fieldMapping).find(
            ([, f]) => f === field.key
          )?.[0];
          if (!csvColumn || !row[csvColumn]?.trim()) {
            return false;
          }
        }
        return true;
      })
      .map(row => {
        const mappedRow: Record<string, string> = {};
        for (const [csvCol, entityField] of Object.entries(state.fieldMapping)) {
          if (entityField && row[csvCol] !== undefined) {
            mappedRow[entityField] = row[csvCol];
          }
        }
        return mappedRow;
      });
  }, [state.parsedData, state.entityType, state.fieldMapping]);

  // Start batch import
  const startImport = useCallback(async () => {
    if (!state.entityType) return;

    const validRows = getValidRowsForImport();
    if (validRows.length === 0) {
      setState(prev => ({ ...prev, error: 'No valid rows to import' }));
      return;
    }

    const batches = chunkArray(validRows, BATCH_SIZE);

    setState(prev => ({
      ...prev,
      step: 'importing',
      error: null,
      importProgress: {
        currentBatch: 0,
        totalBatches: batches.length,
        processedRows: 0,
        totalRows: validRows.length,
      },
      importResult: null,
    }));

    const result: ImportResult = { success: 0, failed: 0, errors: [] };

    for (let i = 0; i < batches.length; i++) {
      const batch = batches[i];
      const batchStartRow = i * BATCH_SIZE;

      try {
        // Dynamic import to avoid circular dependency
        const { api } = await import('@/lib/api');

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let response: any;
        if (state.entityType === 'leads') {
          // Transform 'name' to firstName/lastName for leads
          // Using type assertion since the API validates the data
          const leadsData = batch.map(row => {
            const nameParts = (row.name || '').trim().split(/\s+/);
            const firstName = nameParts[0] || 'Unknown';
            const lastName = nameParts.slice(1).join(' ') || 'Lead';

            // Normalize source value to match API enum
            const normalizeSource = (src: string | undefined): string => {
              if (!src) return 'other';
              const lower = src.toLowerCase().trim();
              const sourceMap: Record<string, string> = {
                website: 'website',
                web: 'website',
                referral: 'referral',
                referred: 'referral',
                cold_call: 'cold_call',
                'cold call': 'cold_call',
                coldcall: 'cold_call',
                trade_show: 'trade_show',
                'trade show': 'trade_show',
                tradeshow: 'trade_show',
                partner: 'partner',
                linkedin: 'other',
                other: 'other',
              };
              return sourceMap[lower] || 'other';
            };

            return {
              firstName,
              lastName,
              email: row.email || undefined,
              phone: row.phone || undefined,
              company: row.company || undefined,
              title: row.title || undefined,
              source: normalizeSource(row.source) as
                | 'website'
                | 'referral'
                | 'cold_call'
                | 'trade_show'
                | 'partner'
                | 'other',
            };
          });
          response = await api.importLeads({
            leads: leadsData,
            skipDuplicates: true,
          });
        } else if (state.entityType === 'accounts') {
          const accountsData = batch.map(row => ({
            name: row.name || '',
            type: (row.type || 'prospect') as 'customer' | 'prospect' | 'partner' | 'competitor',
            industry: (row.industry || undefined) as
              | 'hotel'
              | 'resort'
              | 'guesthouse'
              | 'safari_vessel'
              | 'cruise'
              | 'restaurant'
              | 'government'
              | 'healthcare'
              | 'retail'
              | 'corporate'
              | 'distributor'
              | 'other'
              | undefined,
            website: row.website || undefined,
            phone: row.phone || undefined,
          }));
          response = await api.importAccounts({
            accounts: accountsData,
            skipDuplicates: true,
          });
        } else {
          // Transform 'name' to firstName/lastName for contacts
          const contactsData = batch.map(row => {
            const nameParts = (row.name || '').trim().split(/\s+/);
            const firstName = nameParts[0] || 'Unknown';
            const lastName = nameParts.slice(1).join(' ') || 'Contact';
            return {
              firstName,
              lastName,
              email: row.email || undefined,
              phone: row.phone || undefined,
              title: row.title || undefined,
              accountId: row.account || undefined,
              isPrimary: false,
            };
          });
          response = await api.importContacts({
            contacts: contactsData,
            skipDuplicates: true,
            createMissingAccounts: true,
          });
        }

        result.success += response.successful;
        result.failed += response.failed;

        // Collect errors with original row numbers
        for (const r of response.results) {
          if (!r.success && r.error) {
            result.errors.push({
              row: batchStartRow + r.row + 1, // 1-indexed for display
              message: r.error,
            });
          }
        }
      } catch (error) {
        // If entire batch fails, mark all as failed
        result.failed += batch.length;
        result.errors.push({
          row: batchStartRow + 1,
          message: error instanceof Error ? error.message : 'Batch import failed',
        });
      }

      // Update progress after each batch
      setState(prev => ({
        ...prev,
        importProgress: prev.importProgress
          ? {
              ...prev.importProgress,
              currentBatch: i + 1,
              processedRows: Math.min((i + 1) * BATCH_SIZE, validRows.length),
            }
          : null,
      }));
    }

    // Complete
    setState(prev => ({
      ...prev,
      step: 'complete',
      importResult: result,
      importProgress: null,
    }));
  }, [state.entityType, getValidRowsForImport]);

  return {
    state,
    setEntityType,
    handleFileSelect,
    setFieldMapping,
    getUnmappedRequiredFields,
    proceedToPreview,
    canProceedFromMapping,
    getPreviewData,
    validateRows,
    startImport,
    goBack,
    reset,
  };
}
